import React from "react";
import { GoogleMap, Marker, InfoWindow } from "@react-google-maps/api";

const mapContainerStyle = {
  height: "calc(100vh - 68px)",
  width: "100%",
  marginTop: "68px",
};

const darkMapStyles = [
  { elementType: "geometry", stylers: [{ color: "#0d1117" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#6b7fa3" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#060a0f" }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#1a2035" }] },
  { featureType: "administrative.country", elementType: "labels.text.fill", stylers: [{ color: "#8899bb" }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#7090c0" }] },
  { featureType: "poi", elementType: "geometry", stylers: [{ color: "#0d1a0d" }] },
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#405540" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#0a150a" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#1a2035" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#080b14" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#1b3355" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#0d1e33" }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#3a6fa0" }] },
  { featureType: "transit.line", elementType: "geometry", stylers: [{ color: "#1a2035" }] },
  { featureType: "transit.station", elementType: "geometry", stylers: [{ color: "#1a2535" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#040d18" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#1a3a6a" }] },
];

const ME_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="44" height="58" viewBox="0 0 44 58"><defs><radialGradient id="cg" cx="38%" cy="30%" r="65%"><stop offset="0%" stop-color="#44eeff"/><stop offset="100%" stop-color="#0088aa"/></radialGradient><filter id="gl" x="-40%" y="-40%" width="180%" height="180%"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs><ellipse cx="22" cy="55" rx="8" ry="3" fill="rgba(0,0,0,0.4)"/><path d="M22 3C11 3 3 11 3 22c0 15 19 33 19 33S41 37 41 22C41 11 33 3 22 3z" fill="url(#cg)" filter="url(#gl)"/><circle cx="22" cy="22" r="9.5" fill="rgba(8,11,20,0.92)"/><circle cx="22" cy="22" r="5" fill="#00d4ff"/><circle cx="22" cy="22" r="2" fill="white"/></svg>`;

const OTHER_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="48" viewBox="0 0 36 48"><defs><radialGradient id="vg" cx="38%" cy="30%" r="65%"><stop offset="0%" stop-color="#b06cff"/><stop offset="100%" stop-color="#5a10b8"/></radialGradient><filter id="vgl" x="-40%" y="-40%" width="180%" height="180%"><feGaussianBlur stdDeviation="2" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs><ellipse cx="18" cy="45" rx="6" ry="2.5" fill="rgba(0,0,0,0.4)"/><path d="M18 2C8 2 2 9 2 18c0 12 16 28 16 28S34 30 34 18C34 9 28 2 18 2z" fill="url(#vg)" filter="url(#vgl)"/><circle cx="18" cy="18" r="7.5" fill="rgba(8,11,20,0.9)"/><circle cx="18" cy="18" r="3.5" fill="#9d4edd"/><circle cx="18" cy="18" r="1.4" fill="white"/></svg>`;

const DEFAULT_AVATAR = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" rx="12" fill="#161b2e"/><circle cx="32" cy="24" r="11" fill="#2a3a5c"/><ellipse cx="32" cy="54" rx="18" ry="12" fill="#2a3a5c"/></svg>`)}`;


const Map = ({
  center,
  userLocation,
  otherUsersData,
  showInfoWindow,
  activeUserId,
  handleMarkerClick,
  handleInfoWindowClose,
  userData,
  profileImageUrl,
}) => {
  const icon = (path, color = "rgba(0,212,255,0.7)") => (
    `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="${color}" style="flex-shrink:0;margin-top:1px"><path d="${path}"/></svg>`
  );
  const EMAIL_PATH = "M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z";
  const PHONE_PATH = "M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z";
  const LOC_PATH = "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z";

  const card = {
    wrap: { background: "transparent", padding: "10px 8px 8px", minWidth: "230px", maxWidth: 260, fontFamily: "'Roboto', -apple-system, sans-serif" },
    header: { display: "flex", alignItems: "center", gap: 10, marginBottom: 10 },
    avatar: { width: 54, height: 54, borderRadius: 13, objectFit: "cover", flexShrink: 0, border: "2px solid rgba(0,212,255,0.38)", boxShadow: "0 0 16px rgba(0,212,255,0.22)" },
    avatarViolet: { width: 54, height: 54, borderRadius: 13, objectFit: "cover", flexShrink: 0, border: "2px solid rgba(157,78,221,0.45)", boxShadow: "0 0 16px rgba(157,78,221,0.25)" },
    name: { fontWeight: 700, fontSize: 15, color: "#00d4ff", lineHeight: 1.2 },
    nameViolet: { fontWeight: 700, fontSize: 15, color: "#b06cff", lineHeight: 1.2 },
    badge: { marginTop: 5, display: "inline-block", background: "rgba(0,212,255,0.1)", border: "1px solid rgba(0,212,255,0.28)", borderRadius: 20, padding: "2px 9px", fontSize: 11, color: "rgba(0,212,255,0.85)", letterSpacing: "0.3px" },
    badgeViolet: { marginTop: 5, display: "inline-block", background: "rgba(157,78,221,0.12)", border: "1px solid rgba(157,78,221,0.32)", borderRadius: 20, padding: "2px 9px", fontSize: 11, color: "rgba(157,78,221,0.9)", letterSpacing: "0.3px" },
    divider: { height: 1, background: "rgba(255,255,255,0.07)", margin: "8px 0" },
    row: { fontSize: 12, color: "rgba(185,205,255,0.7)", lineHeight: "22px", display: "flex", alignItems: "center", gap: 7 },
    btn: { marginTop: 10, width: "100%", padding: "9px 0", borderRadius: 11, border: "1px solid rgba(157,78,221,0.35)", background: "linear-gradient(135deg, rgba(0,212,255,0.12), rgba(123,47,247,0.18))", color: "#b06cff", fontWeight: 700, fontSize: 12.5, cursor: "pointer", letterSpacing: "0.4px", fontFamily: "inherit", transition: "all 0.2s" },
    headerLink: { display: "flex", alignItems: "center", gap: 10, marginBottom: 10, cursor: "pointer", textDecoration: "none" },
  };

  const Row = ({ path, text, color }) => text ? (
    <div style={card.row}>
      <span dangerouslySetInnerHTML={{ __html: icon(path, color) }} />
      <span>{text}</span>
    </div>
  ) : null;

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={center}
      zoom={14}
      options={{ styles: darkMapStyles, disableDefaultUI: true }}
    >
      {userLocation && (
        <Marker
          position={userLocation}
          onClick={() => handleMarkerClick(null)}
          icon={{
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(ME_SVG)}`,
            scaledSize: new window.google.maps.Size(44, 58),
            anchor: new window.google.maps.Point(22, 55),
          }}
        >
          {showInfoWindow && activeUserId === null && (
            <InfoWindow onCloseClick={handleInfoWindowClose}>
              <div style={card.wrap}>
                {userData && (
                  <>
                    <a href="/profile" style={card.headerLink}>
                      <img src={profileImageUrl || DEFAULT_AVATAR} alt="" style={card.avatar} />
                      <div>
                        <div style={card.name}>{userData.displayName}</div>
                        {userData.profession && <div style={card.badge}>{userData.profession}</div>}
                      </div>
                    </a>
                    <div style={card.divider} />
                    <Row path={EMAIL_PATH} text={userData.email} color="rgba(0,212,255,0.65)" />
                    <Row path={PHONE_PATH} text={userData.phone} color="rgba(0,212,255,0.65)" />
                    <Row path={LOC_PATH} text={userData.point} color="rgba(0,212,255,0.65)" />
                  </>
                )}
              </div>
            </InfoWindow>
          )}
        </Marker>
      )}

      {otherUsersData.map((otherUser) => (
        <Marker
          key={otherUser.id}
          position={otherUser.location}
          onClick={() => handleMarkerClick(otherUser.id)}
          icon={{
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(OTHER_SVG)}`,
            scaledSize: new window.google.maps.Size(36, 48),
            anchor: new window.google.maps.Point(18, 45),
          }}
        >
          {showInfoWindow && activeUserId === otherUser.id && (
            <InfoWindow onCloseClick={handleInfoWindowClose}>
              <div style={card.wrap}>
                {otherUser && (
                  <>
                    <a href={`/perfil/${otherUser.id}`} style={card.headerLink}>
                      <img src={otherUser.profileImageUrl || DEFAULT_AVATAR} alt="" style={card.avatarViolet} />
                      <div>
                        <div style={card.nameViolet}>{otherUser.displayName}</div>
                        {otherUser.profession && <div style={card.badgeViolet}>{otherUser.profession}</div>}
                      </div>
                    </a>
                    <div style={card.divider} />
                    <Row path={EMAIL_PATH} text={otherUser.email} color="rgba(157,78,221,0.7)" />
                    <Row path={PHONE_PATH} text={otherUser.phone} color="rgba(157,78,221,0.7)" />
                    <Row path={LOC_PATH} text={otherUser.point} color="rgba(157,78,221,0.7)" />
                    <button style={card.btn}>Solicitar Encuentro</button>
                  </>
                )}
              </div>
            </InfoWindow>
          )}
        </Marker>
      ))}
    </GoogleMap>
  );
};

export default Map;
